// Manages per-field metadata (editorial status, AI suggestions)
// This data lives outside react-hook-form values
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import type { FieldStatus, FieldMetadata, FieldMetadataMap } from '../types/fields';

// Default metadata for new fields — 'incomplete' ("To start") until the user
// actually edits the field; only then does it move to 'in_progress'.
const DEFAULT_METADATA: FieldMetadata = {
  status: 'incomplete',
};

export interface FieldMetadataContextValue {
  // Get metadata for a field (returns default if not set)
  getMetadata: (fieldName: string) => FieldMetadata;
  // Set editorial status
  setStatus: (fieldName: string, status: FieldStatus) => void;
  // Set AI suggestion (for comparison UI)
  setAISuggestion: (fieldName: string, value: string | null) => void;
  // Set AI loading state
  setAIGenerating: (fieldName: string, loading: boolean) => void;
  // Get all metadata (for persistence)
  getAllMetadata: () => FieldMetadataMap;
}

const FieldMetadataContext = createContext<FieldMetadataContextValue | null>(null);

export interface FieldMetadataProviderProps {
  // Initial metadata (from backend or previous session)
  initialMetadata?: FieldMetadataMap;
  // Callback when metadata changes (for persistence)
  onMetadataChange?: (fieldName: string, metadata: FieldMetadata) => void;
  children: ReactNode;
}

export function FieldMetadataProvider({
  initialMetadata,
  onMetadataChange,
  children,
}: FieldMetadataProviderProps) {
  // Lazy initializer avoids creating a new {} reference on every render
  const [metadata, setMetadata] = useState<FieldMetadataMap>(() => initialMetadata ?? {});

  // Queue for deferred parent notifications (avoids setState during render)
  const pendingNotifyRef = useRef<{ fieldName: string; metadata: FieldMetadata } | null>(null);

  // Sync when parent explicitly pushes new metadata (e.g. after AI regeneration)
  // Guard against undefined: omitting the prop yields undefined (stable), not a new {} each render
  useEffect(() => {
    if (initialMetadata !== undefined) {
      setMetadata(initialMetadata);
    }
  }, [initialMetadata]);

  // Flush pending notification after render completes
  useEffect(() => {
    if (pendingNotifyRef.current && onMetadataChange) {
      const { fieldName, metadata: meta } = pendingNotifyRef.current;
      pendingNotifyRef.current = null;
      onMetadataChange(fieldName, meta);
    }
  });

  // Get metadata for a field, with default fallback
  const getMetadata = useCallback(
    (fieldName: string): FieldMetadata => {
      return metadata[fieldName] || DEFAULT_METADATA;
    },
    [metadata]
  );

  // Update single field's metadata and defer parent notification
  const updateField = useCallback((fieldName: string, updates: Partial<FieldMetadata>) => {
    setMetadata((prev) => {
      const current = prev[fieldName] || DEFAULT_METADATA;
      const updated = { ...current, ...updates };
      // Defer notification to avoid setState-during-render warning
      pendingNotifyRef.current = { fieldName, metadata: updated };
      return { ...prev, [fieldName]: updated };
    });
  }, []);

  const setStatus = useCallback(
    (fieldName: string, status: FieldStatus) => {
      updateField(fieldName, { status });
    },
    [updateField]
  );

  const setAISuggestion = useCallback(
    (fieldName: string, value: string | null) => {
      updateField(fieldName, {
        aiSuggestion: value ?? undefined,
        // Auto-set status to ai_review when AI generates content, remembering
        // the prior status so "keep current" can restore it later
        ...(value
          ? {
              status: 'ai_review' as FieldStatus,
              previousStatus: metadata[fieldName]?.status ?? ('in_progress' as FieldStatus),
            }
          : {}),
      });
    },
    [updateField, metadata]
  );

  const setAIGenerating = useCallback(
    (fieldName: string, loading: boolean) => {
      updateField(fieldName, { isAIGenerating: loading });
    },
    [updateField]
  );

  const getAllMetadata = useCallback(() => metadata, [metadata]);

  const value = useMemo<FieldMetadataContextValue>(
    () => ({
      getMetadata,
      setStatus,
      setAISuggestion,
      setAIGenerating,
      getAllMetadata,
    }),
    [getMetadata, setStatus, setAISuggestion, setAIGenerating, getAllMetadata]
  );

  return <FieldMetadataContext.Provider value={value}>{children}</FieldMetadataContext.Provider>;
}

// Hook to access field metadata context
export function useFieldMetadata(): FieldMetadataContextValue {
  const ctx = useContext(FieldMetadataContext);
  if (!ctx) {
    throw new Error('useFieldMetadata must be used within FieldMetadataProvider');
  }
  return ctx;
}
